/*
 * Pieces every chart shares: the empty state and the tooltip.
 *
 * Both are ported from html-markup/assets/js/charts.js — the empty state from
 * `empty()`, the tooltip from the TIP class string every hover target used.
 * Recharts' default tooltip is a white box with inline styles that ignores the
 * theme entirely, so it is replaced rather than restyled.
 */

/** Shown instead of a chart when there is nothing to draw. */
export function ChartEmpty({ message = "No data for this range" }) {
    return (
        <div
            data-chart-empty
            className="flex flex-col items-center gap-2 py-12 text-slate-400 dark:text-slate-500"
        >
            <svg
                className="size-8 opacity-50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                aria-hidden="true"
            >
                <path d="M3 3v18h18" />
                <path d="m7 14 4-4 3 3 5-6" />
            </svg>
            <p className="text-[13px]">{message}</p>
        </div>
    );
}

/**
 * Recharts tooltip content.
 *
 * `format` applies to values only. The label is whatever the caller put in
 * `label`, already human-readable — formatting it again would mangle dates.
 */
export function ChartTooltip({ active, payload, label, format = (v) => v, showNames }) {
    if (!active || !payload?.length) return null;

    // Recharts keeps entries for hidden series in the payload; drop them so a
    // toggled-off series does not linger in the tooltip.
    const rows = payload.filter((p) => p.value != null);
    if (!rows.length) return null;

    return (
        <div
            role="tooltip"
            data-chart-tooltip
            className="pointer-events-none rounded bg-slate-800 px-2 py-1 text-[11.5px] whitespace-nowrap text-white shadow dark:bg-slate-700"
        >
            <span className="font-medium">{label}</span>
            {rows.map((row) => (
                <span key={row.dataKey ?? row.name} className="ml-1.5">
                    ·{" "}
                    {showNames && row.name ? (
                        <span className="opacity-75">{row.name}: </span>
                    ) : null}
                    {format(row.value)}
                </span>
            ))}
        </div>
    );
}
